import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["event", "trackingProperties", "useBeacon"];
import { beaconTrack, track } from '../track';
import { useCallback } from 'react';
import debug from 'growth-payments-core/debug/console';
import { jsx as _jsx } from "react/jsx-runtime";
/**
 * A Higher-level-component that overrides an event handler and tracks the event before calling the original handler.
 *
 * Usage:
 * ```tsx
 * const TrackedButton = trackedProp('button', 'onClick');
 * ...
 * <TrackedButton
 *  event={events.paymentSettingsInteraction}
 *  trackingProperties={{
 *    action: 'setUpHubspotPaymentsClicked',
 *  }}
 *  onClick={()=>alert('clicked')}
 * />
 * ```
 * @param Component The component to override the event handler on
 * @param propName The name of the event handler prop to override
 * @returns
 */

const trackedProp = (Component, propName) => {
  return _ref => {
    let {
        event,
        trackingProperties,
        useBeacon
      } = _ref,
      componentProps = _objectWithoutPropertiesLoose(_ref, _excluded);
    // casting here becuase we get a redundant type (Omit<PropTypes & {event, trackingProperties}, 'event' | 'trackingProperties'>)
    const props = componentProps;
    const func = props[propName];
    // If the prop is not a function, we don't want to override it
    if (func && typeof func !== 'function') {
      debug.error(`trackedProp: ${String(propName)} is not a function on ${typeof Component === 'function' ? Component.name : Component}`);
      return /*#__PURE__*/_jsx(Component, Object.assign({}, props));
    }
    const trackFunction = useBeacon ? beaconTrack : track;

    // Create the new event handler
    const callback = useCallback((...params) => {
      trackFunction(event, trackingProperties);
      if (func) func(...params);
    }, [event, trackingProperties, func, trackFunction]);
    // Construct the new props object
    const newProps = Object.assign({}, props, {
      [propName]: callback
    });
    return /*#__PURE__*/_jsx(Component, Object.assign({}, newProps));
  };
};
export default trackedProp;